import styles from './WorkspaceOpportunityDetailsSheet.module.css'
import { useScrollOffsets } from '@/hooks/element-hooks'
import { SideSheet } from '@/components/SideSheet'
import {
  SideSheetHeader,
  SideSheetHeaderContent,
  SideSheetHeaderAvatar,
  SideSheetHeaderText,
  SideSheetHeaderTitle,
  SideSheetHeaderDescription,
} from '@/components/SideSheetHeader'
import { Button } from '@/components/Button'
import { NoOpportunitySelected } from '@/features/opportunities/components/OpportunityDetailsSheet/NoOpportunitySelected'
import {
  OpportunityDetailsContentItem,
  OpportunityDetailsContent,
} from '@/features/opportunities/components/OpportunityDetailsContent'
import { OpportunityDetailsActions } from '@/features/opportunities/components/OpportunityDetailsActions'
import { ButtonCollection } from '@/components/ButtonCollection'

export interface WorkspaceOpportunityDetails {
  jobTypes: string
  isApplicant: boolean
  specialties: string
  description: string
  requirements: string
  location: string | null | undefined
}

export interface WorkspaceOpportunityDetailsSheetProps {
  opportunity: WorkspaceOpportunityDetails | null
  onClose?: () => void
  isWithdrawPending: boolean
  onWithdraw: () => unknown
  isApplyPending: boolean
  canApply: boolean
  onApply: () => unknown
  corporateMode: boolean
}

export function WorkspaceOpportunityDetailsSheet({
  opportunity,
  onClose,
  isWithdrawPending,
  onWithdraw,
  isApplyPending,
  canApply,
  onApply,
  corporateMode,
}: WorkspaceOpportunityDetailsSheetProps) {
  const { connectRef: rootRef, scrollYStart: scrollOffset } = useScrollOffsets()
  return (
    <SideSheet>
      <div className={styles.root} ref={rootRef}>
        {!opportunity ? (
          <NoOpportunitySelected corporateMode={corporateMode} />
        ) : (
          <>
            <SideSheetHeader
              bordered
              onClose={onClose}
              scrollOffset={scrollOffset}
            >
              <SideSheetHeaderContent>
                <SideSheetHeaderAvatar
                  subject={{
                    avatar: undefined,
                  }}
                />
                <SideSheetHeaderText>
                  <SideSheetHeaderTitle>Firm Opportunity</SideSheetHeaderTitle>
                  <SideSheetHeaderDescription>
                    {opportunity.jobTypes || 'No description'}
                  </SideSheetHeaderDescription>
                  <br />
                </SideSheetHeaderText>
                <OpportunityDetailsActions>
                  <ButtonCollection fullWidth>
                    {opportunity.isApplicant ? (
                      <Button
                        label="I'm no longer interested"
                        variant="destructive"
                        isPending={isWithdrawPending}
                        isDisabled={isWithdrawPending}
                        onPress={onWithdraw}
                        testId="workspace-opportunity-withdraw-button"
                        block
                      />
                    ) : (
                      <Button
                        label="I'm interested"
                        variant="primary"
                        isPending={isApplyPending}
                        isDisabled={isApplyPending || !canApply}
                        onPress={onApply}
                        testId="workspace-opportunity-apply-button"
                        block
                      />
                    )}
                  </ButtonCollection>
                </OpportunityDetailsActions>
              </SideSheetHeaderContent>
            </SideSheetHeader>
            <Content opportunity={opportunity} />
          </>
        )}
      </div>
    </SideSheet>
  )
}

interface ContentProps {
  opportunity: WorkspaceOpportunityDetails
}

function Content({ opportunity }: ContentProps) {
  const children = (
    <>
      <OpportunityDetailsContentItem title="Skills">
        {opportunity.specialties}
      </OpportunityDetailsContentItem>
      <OpportunityDetailsContentItem title="Description">
        {opportunity.description}
      </OpportunityDetailsContentItem>
      <OpportunityDetailsContentItem title="Software Requirements">
        {opportunity.requirements}
      </OpportunityDetailsContentItem>
      {opportunity.location && (
        <OpportunityDetailsContentItem title="Location">
          {opportunity.location}
        </OpportunityDetailsContentItem>
      )}
    </>
  )

  return (
    <OpportunityDetailsContent>
      {children}
      {/*
        There should always be children because some fields are required.
       */}
    </OpportunityDetailsContent>
  )
}
